import { Particle, ParticleContainer, Rectangle, Sprite, Texture } from 'pixi.js';
import { INTERNAL_HEIGHT, INTERNAL_WIDTH, POOL_CAPACITY } from '../../../constants';
import type { GameWorld } from '../../../world/GameWorld';
import type { MobilePoolSnapshot } from '../MobilePoolObserver';
import { WORLD_TO_R2 } from '../scale';
import type { EntityFamily, FamilyDeps, FrameContext } from './types';

/** ParticlePool.color is a small palette index, not a colour value. */
const PARTICLE_COLORS: readonly number[] = Object.freeze([
  0xd8f4ff, 0x70fff2, 0xffd76a, 0x9db4ff, 0xff8ab0, 0xffffff, 0x62e6ff, 0xff5f7a,
]);
const COLOR_MASK = 7;
const MIN_PARTICLE_SIZE = 2;
const WHITE_WIDTH = Math.max(1, Texture.WHITE.width);
const WHITE_HEIGHT = Math.max(1, Texture.WHITE.height);

/**
 * Desktop keeps one Sprite(Texture.WHITE) per sim slot. Mobile uses the PixiJS
 * v8 ParticleContainer path: every lightweight Particle is allocated before
 * gameplay and remains in one GPU batch, so explosions never allocate Sprites
 * or rebuild the scene graph mid-frame.
 */
export class ParticlesFamily implements EntityFamily {
  private mobilePerformanceMode = false;
  private readonly sprites: Sprite[] = [];
  private mobileContainer: ParticleContainer | null = null;
  private readonly mobileParticles: Particle[] = [];
  private mobilePool: MobilePoolSnapshot | null = null;

  init(deps: FamilyDeps): void {
    this.mobilePerformanceMode = deps.mobilePerformanceMode;
    this.mobilePool = deps.mobilePools?.particles ?? null;
    if (this.mobilePerformanceMode) {
      for (let i = 0; i < POOL_CAPACITY.particles; i += 1) {
        this.mobileParticles.push(new Particle({ texture: Texture.WHITE, alpha: 0 }));
      }
      const container = new ParticleContainer({
        texture: Texture.WHITE,
        particles: this.mobileParticles,
        boundsArea: new Rectangle(
          0,
          0,
          INTERNAL_WIDTH * WORLD_TO_R2,
          INTERNAL_HEIGHT * WORLD_TO_R2,
        ),
        roundPixels: true,
        dynamicProperties: {
          position: true,
          vertex: true,
          rotation: false,
          uvs: false,
          color: true,
        },
      });
      deps.layer.addChild(container);
      this.mobileContainer = container;
      return;
    }
    for (let i = 0; i < POOL_CAPACITY.particles; i += 1) {
      const sprite = createParticleSprite();
      sprite.visible = false;
      deps.layer.addChild(sprite);
      this.sprites.push(sprite);
    }
  }

  sync(world: GameWorld, _frame: FrameContext): void {
    if (this.mobilePerformanceMode) {
      this.syncMobile(world);
      return;
    }
    const pool = world.particles;
    for (let i = 0; i < this.sprites.length; i += 1) {
      const sprite = this.sprites[i]!;
      if (pool.indices.active[i] === 0) {
        sprite.visible = false;
        continue;
      }
      const remaining = pool.life[i]! / Math.max(1, pool.maxLife[i]!);
      const size = Math.max(MIN_PARTICLE_SIZE, Math.round(pool.size[i]! * WORLD_TO_R2));
      sprite.visible = true;
      sprite.tint = PARTICLE_COLORS[pool.color[i]! & COLOR_MASK]!;
      sprite.alpha = Math.max(0, Math.min(1, remaining));
      sprite.setSize(size, size);
      sprite.position.set(
        Math.round(pool.x[i]! * WORLD_TO_R2 - size / 2),
        Math.round(pool.y[i]! * WORLD_TO_R2 - size / 2),
      );
    }
  }

  dispose(): void {
    this.mobileParticles.length = 0;
    this.mobileContainer = null;
    this.mobilePool = null;
    this.mobilePerformanceMode = false;
    this.sprites.length = 0;
  }

  private syncMobile(world: GameWorld): void {
    if (!this.mobileContainer) return;
    const observed = this.mobilePool;
    if (!observed) return;
    for (let i = 0; i < observed.deactivatedCount; i += 1) {
      this.mobileParticles[observed.deactivatedSlots[i]!]!.alpha = 0;
    }
    for (let i = 0; i < observed.activeCount; i += 1) {
      const slot = observed.activeSlots[i]!;
      drawMobileParticle(world, slot, this.mobileParticles[slot]!);
    }
  }
}

function createParticleSprite(): Sprite {
  const sprite = new Sprite(Texture.WHITE);
  sprite.roundPixels = true;
  sprite.visible = false;
  return sprite;
}

function drawParticle(world: GameWorld, i: number, sprite: Sprite): void {
  const pool = world.particles;
  const remaining = pool.life[i]! / Math.max(1, pool.maxLife[i]!);
  const size = Math.max(MIN_PARTICLE_SIZE, Math.round(pool.size[i]! * WORLD_TO_R2));
  sprite.visible = true;
  sprite.tint = PARTICLE_COLORS[pool.color[i]! & COLOR_MASK]!;
  sprite.alpha = Math.max(0, Math.min(1, remaining));
  sprite.setSize(size, size);
  sprite.position.set(
    Math.round(pool.x[i]! * WORLD_TO_R2 - size / 2),
    Math.round(pool.y[i]! * WORLD_TO_R2 - size / 2),
  );
}

function drawMobileParticle(world: GameWorld, i: number, particle: Particle): void {
  const pool = world.particles;
  const remaining = pool.life[i]! / Math.max(1, pool.maxLife[i]!);
  const size = Math.max(MIN_PARTICLE_SIZE, Math.round(pool.size[i]! * WORLD_TO_R2));
  particle.tint = PARTICLE_COLORS[pool.color[i]! & COLOR_MASK]!;
  particle.alpha = Math.max(0, Math.min(1, remaining));
  particle.scaleX = size / WHITE_WIDTH;
  particle.scaleY = size / WHITE_HEIGHT;
  particle.x = Math.round(pool.x[i]! * WORLD_TO_R2 - size / 2);
  particle.y = Math.round(pool.y[i]! * WORLD_TO_R2 - size / 2);
}
